import { useState, useRef, useCallback, useEffect } from "react";
import { Send, Mic, Loader2, X, Camera, Paperclip, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isDespiaNative, openIOSSettings } from "@/utils/despia";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ChatInputProps {
  onSend: (message: string, mediaUrl?: string, mediaType?: 'image' | 'video') => void;
  isLoading: boolean;
  disabled?: boolean;
  userId?: string;
  hasWorkoutButton?: boolean;
  onMicClick?: () => void;
  isListening?: boolean;
  showMicButton?: boolean;
}

type PermissionType = 'camera' | 'microphone' | 'photos';

export function ChatInput({ onSend, isLoading, disabled, userId, hasWorkoutButton, onMicClick, isListening, showMicButton = true }: ChatInputProps) {
  const [input, setInput] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [mediaPreview, setMediaPreview] = useState<{ url: string; type: 'image' | 'video'; file: File } | null>(null);
  const [permissionDialog, setPermissionDialog] = useState<{
    open: boolean;
    type: PermissionType;
    title: string;
    description: string;
  } | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  // Track active MediaStream to clean up properly
  const activeStreamRef = useRef<MediaStream | null>(null);
  // Track component mount state for cleanup
  const isMountedRef = useRef(true);

  // Cleanup active streams on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanupActiveStream();
    };
  }, []);

  /**
   * Clean up any active MediaStream to free device resources
   */
  const cleanupActiveStream = useCallback(() => {
    if (activeStreamRef.current) {
      console.log('[ChatInput] Cleaning up active stream');
      activeStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      activeStreamRef.current = null;
    }
  }, []);

  const showPermissionDeniedDialog = useCallback((type: PermissionType) => {
    const messages: Record<PermissionType, { title: string; description: string }> = {
      camera: {
        title: "Camera Access Required",
        description: "Please enable camera access in Settings to take photos and videos. Go to Settings > Privacy & Security > Camera and enable access for this app."
      },
      microphone: {
        title: "Microphone Access Required", 
        description: "Please enable microphone access in Settings to use voice input. Go to Settings > Privacy & Security > Microphone and enable access for this app."
      },
      photos: {
        title: "Photo Library Access Required",
        description: "Please enable photo library access in Settings to select photos and videos. Go to Settings > Privacy & Security > Photos and enable access for this app."
      }
    };
    
    setPermissionDialog({
      open: true,
      type,
      ...messages[type]
    });
  }, []);

  const handleOpenSettings = useCallback(() => {
    if (isDespiaNative()) {
      openIOSSettings();
    }
    setPermissionDialog(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && !mediaPreview) || isLoading || disabled) return;

    let mediaUrl: string | undefined;
    let mediaType: 'image' | 'video' | undefined;

    if (mediaPreview && userId) {
      setIsUploading(true);
      try {
        const fileExt = mediaPreview.file.name.split('.').pop();
        const fileName = `${userId}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('chat-media')
          .upload(fileName, mediaPreview.file);

        if (uploadError) throw uploadError;

        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from('chat-media')
          .createSignedUrl(fileName, 3600);

        if (signedUrlError) throw signedUrlError;
        mediaUrl = signedUrlData.signedUrl;
        mediaType = mediaPreview.type;
      } catch (error) {
        console.error('Upload error:', error);
        toast.error("Failed to upload media");
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    const message = input.trim() || (mediaPreview ? `[${mediaPreview.type === 'video' ? 'Video' : 'Image'} attached]` : '');
    onSend(message, mediaUrl, mediaType);
    setInput("");
    setMediaPreview(null);
  };

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('[ChatInput] File selected:', file?.name, file?.type, file?.size);
    
    // Reset input immediately for re-use
    if (e.target) {
      e.target.value = '';
    }
    
    if (!file) {
      console.log('[ChatInput] No file selected');
      return;
    }

    if (file.size > 52428800) {
      toast.error("File too large. Maximum size is 50MB.");
      return;
    }

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      toast.error("Please select an image or video file");
      return;
    }

    const url = URL.createObjectURL(file);
    setMediaPreview({
      url,
      type: isVideo ? 'video' : 'image',
      file,
    });
  }, []);

  const clearMediaPreview = useCallback(() => {
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview.url);
      setMediaPreview(null);
    }
  }, [mediaPreview]);

  /**
   * Trigger camera input directly.
   * On iOS WKWebView: The capture="environment" attribute on the input element
   * triggers the native camera permission dialog automatically when clicked.
   * We must NOT use async/await before the click, as that breaks the user gesture chain.
   * The file input with capture attribute handles camera permissions natively.
   */
  const handleCameraClick = useCallback(() => {
    console.log('[ChatInput] Camera button clicked, isDespiaNative:', isDespiaNative());
    
    // Clean up any previous stream first
    cleanupActiveStream();
    
    // CRITICAL: On iOS, we must trigger the file input click SYNCHRONOUSLY
    // within the user gesture. Using async/await before this breaks the gesture chain
    // and iOS will ignore the programmatic click.
    // The capture="environment" attribute on cameraInputRef handles camera permission
    // requests automatically through the native file picker.
    
    if (cameraInputRef.current) {
      // Force reset the input to allow selecting same file again
      cameraInputRef.current.value = '';
      // Trigger immediately - minimal delay for DOM readiness
      // Using requestAnimationFrame instead of setTimeout for better iOS compatibility
      requestAnimationFrame(() => {
        if (cameraInputRef.current && isMountedRef.current) {
          console.log('[ChatInput] Triggering camera input click');
          cameraInputRef.current.click();
        }
      });
    }
  }, [cleanupActiveStream]);

  /**
   * Handle file/photo library picker.
   * On iOS/iPadOS: Must trigger input click SYNCHRONOUSLY within user gesture.
   * Using setTimeout or async breaks the gesture chain and causes unresponsiveness.
   */
  const handleFileClick = useCallback(() => {
    console.log('[ChatInput] File button clicked');
    
    // CRITICAL: On iOS/iPadOS, trigger file input click SYNCHRONOUSLY
    // Using setTimeout breaks the user gesture chain and causes hangs
    if (fileInputRef.current) {
      // Force reset the input to allow selecting same file again
      fileInputRef.current.value = '';
      // Use requestAnimationFrame for minimal delay while preserving gesture chain
      requestAnimationFrame(() => {
        if (fileInputRef.current && isMountedRef.current) {
          console.log('[ChatInput] Triggering file input click');
          fileInputRef.current.click();
        }
      });
    }
  }, []);

  /**
   * Handle microphone button click.
   * On iOS/iPadOS: Permission request must happen synchronously within user gesture.
   * CRITICAL: Do NOT use async/await - it breaks the gesture chain on iOS.
   */
  const handleMicClickInternal = useCallback(() => {
    console.log('[ChatInput] Mic button clicked, isDespiaNative:', isDespiaNative());
    
    // Clean up any previous stream first
    cleanupActiveStream();
    
    // On iOS native, request microphone permission via getUserMedia
    // CRITICAL: Use .then() instead of async/await to preserve gesture chain
    if (isDespiaNative() && navigator.mediaDevices?.getUserMedia) {
      console.log('[ChatInput] Requesting microphone permission via getUserMedia');
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => {
          // Store reference for cleanup
          activeStreamRef.current = stream;
          // Immediately stop the stream - we just needed to trigger the permission
          cleanupActiveStream();
          console.log('[ChatInput] Microphone permission granted, stream cleaned up');
          // Call the original mic click handler after permission granted
          onMicClick?.();
        })
        .catch((error: any) => {
          console.error('[ChatInput] Microphone permission error:', error);
          cleanupActiveStream();
          
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            showPermissionDeniedDialog('microphone');
            return;
          }
          // For other errors, still try to proceed with voice input
          console.log('[ChatInput] Continuing despite mic error:', error.name);
          onMicClick?.();
        });
    } else {
      // Non-native or no getUserMedia, call handler directly
      onMicClick?.();
    }
  }, [cleanupActiveStream, showPermissionDeniedDialog, onMicClick]);

  const bottomPosition = hasWorkoutButton ? 'bottom-[8.5rem]' : 'bottom-[4rem]';
  const isInputDisabled = isLoading || disabled || isUploading;
  
  return (
    <>
      <form 
        id="chat-input-container"
        onSubmit={handleSubmit}
        className={`bg-background p-4 fixed left-0 right-0 z-40 safe-area-bottom-padded ${bottomPosition}`}
      >
        {/* Media Preview */}
        {mediaPreview && (
          <div className="mb-3 relative inline-block">
            {mediaPreview.type === 'video' ? (
              <video 
                src={mediaPreview.url} 
                className="h-20 rounded-lg border border-border/50"
                muted
              />
            ) : (
              <img 
                src={mediaPreview.url} 
                alt="Preview" 
                className="h-20 rounded-lg border border-border/50"
              />
            )}
            <button
              type="button"
              onClick={clearMediaPreview}
              className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        <div className="flex items-start gap-2">
          <div className="flex-1 relative">
            <textarea
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e as unknown as React.FormEvent);
                }
              }}
              placeholder={disabled ? "Upgrade..." : "Message coach..."}
              disabled={isInputDisabled}
              rows={1}
              className="w-full bg-secondary/50 border border-border/50 rounded-xl px-3 py-3 pr-[6.5rem] text-base placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all resize-none overflow-hidden min-h-[48px] max-h-[120px]"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 touch-manipulation"
                disabled={isInputDisabled}
                onClick={handleCameraClick}
                aria-label="Take photo or video"
              >
                <Camera className="w-4 h-4 text-muted-foreground" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 touch-manipulation"
                disabled={isInputDisabled}
                onClick={handleFileClick}
                aria-label="Attach file"
              >
                <Paperclip className="w-4 h-4 text-muted-foreground" />
              </Button>
              {showMicButton && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 touch-manipulation ${isListening ? 'bg-destructive/20 animate-pulse' : ''}`}
                  disabled={isLoading || disabled}
                  onClick={handleMicClickInternal}
                  aria-label="Voice input"
                >
                  <Mic className={`w-4 h-4 ${isListening ? 'text-destructive' : 'text-muted-foreground'}`} />
                </Button>
              )}
            </div>
          </div>
          <Button 
            type="submit" 
            variant="evolve" 
            size="icon"
            disabled={(!input.trim() && !mediaPreview) || isInputDisabled}
            className="h-12 w-12 touch-manipulation"
            aria-label="Send message"
          >
            {isLoading || isUploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
        
        {/* File input for gallery/files - no capture attribute */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleFileSelect}
          className="hidden"
          aria-hidden="true"
        />
        {/* Camera input with capture attribute for direct camera access */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*,video/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
          aria-hidden="true"
        />
        
        {disabled && (
          <p className="text-xs text-center text-primary mt-2 font-medium">
            Daily message limit reached. Upgrade to Premium for unlimited access.
          </p>
        )}
      </form>

      {/* Permission Denied Dialog */}
      <AlertDialog open={permissionDialog?.open} onOpenChange={(open) => !open && setPermissionDialog(null)}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{permissionDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              {permissionDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setPermissionDialog(null)}>
              Cancel
            </AlertDialogCancel>
            {isDespiaNative() && (
              <AlertDialogAction onClick={handleOpenSettings} className="gap-2">
                <Settings className="w-4 h-4" />
                Open Settings
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
